/**
 * The reactive slice of a drag session, as a pure finite-state machine.
 *
 * Only what the React tree must re-render on lives here: the status, the active
 * draggable, and the current `over` droppable (bucket highlight + announcements).
 * Everything high-frequency — pointer x/y, cached rects, the armed-but-not-yet-
 * dragging `pending` phase — lives in refs inside `DndContext`, never in this
 * state, so a pointermove storm doesn't thrash the tree.
 *
 * Both sensors (pointer, keyboard) dispatch the same four actions, so the
 * transition table is the single source of truth for legal drag lifecycles and
 * is trivially unit-testable with no DOM.
 *
 * Side effects (firing `onDragEnd` / `onDragCancel`, focus restoration) are the
 * provider's job — it reads `active`/`over` *before* dispatching the terminal
 * action. The reducer only moves between states.
 */
import type { DragActive, DragInputMode, DragOver, DndStatus } from './types.ts'

export interface DndState {
  status: DndStatus
  /** Which input source owns the drag; `null` when idle. */
  mode: DragInputMode | null
  active: DragActive | null
  over: DragOver | null
}

export const initialDndState: DndState = {
  status: 'idle',
  mode: null,
  active: null,
  over: null,
}

export type DndAction =
  | { type: 'DRAG_START'; mode: DragInputMode; active: DragActive; over: DragOver | null }
  | { type: 'DRAG_OVER'; over: DragOver | null }
  | { type: 'DRAG_END' }
  | { type: 'DRAG_CANCEL' }

function sameOver(a: DragOver | null, b: DragOver | null): boolean {
  if (a === null || b === null) return a === b
  return a.id === b.id
}

export function dndReducer(state: DndState, action: DndAction): DndState {
  switch (action.type) {
    case 'DRAG_START':
      // Re-entrancy guard: a second pickup while already dragging is ignored.
      if (state.status === 'dragging') return state
      return {
        status: 'dragging',
        mode: action.mode,
        active: action.active,
        over: action.over,
      }

    case 'DRAG_OVER':
      // A stray over-update outside a drag is a no-op (e.g. a late pointermove
      // after cancel). Identical `over` returns the same reference so React
      // bails out of the re-render.
      if (state.status !== 'dragging') return state
      if (sameOver(state.over, action.over)) return state
      return { ...state, over: action.over }

    case 'DRAG_END':
    case 'DRAG_CANCEL':
      // Terminal actions while idle are no-ops (a late pointerup after a cancel,
      // a double-drop). Either one returns to a clean idle state.
      if (state.status !== 'dragging') return state
      return initialDndState

    default:
      return state
  }
}
